import { createSignClient } from 'cwj-sign-sdk'
import type {
  ResponseDecision,
  SignClientOptions,
} from 'cwj-sign-sdk'
import {
  buildSignatureText,
  extractSignParamValues,
  extractUrlLastSegment,
} from 'cwj-sign-sdk/debug'

const SIGN_HEADER_NAMES = new Set([
  '_yy_ts',
  '_yy_nonce',
  '_yy_sign',
  '_yy_sign_text',
])
const SIGN_PATH_PATTERN = /^(body|headers)(?:\.(?:\*|[^.[\]]+))+$/

type RequestHeaders = Record<string, unknown>

interface CreateHeadersInput {
  url: string
  body?: unknown
  headers?: RequestHeaders
  signParams: readonly string[]
}

interface SignV3Headers {
  _yy_ts: string
  _yy_nonce: string
  _yy_sign: string
  _yy_sign_text: string
}

interface SignV3Runtime {
  createHeaders(input: CreateHeadersInput): Promise<SignV3Headers>
  processResponse(data: unknown, retryCount: 0 | 1): ResponseDecision
}

export type SignV3RequestConfig = Record<string, unknown> & {
  data?: unknown
  headers?: RequestHeaders
  params?: unknown
}

const cloneValue = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(cloneValue)
  }

  if (Object.prototype.toString.call(value) === '[object Object]') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .map(([key, item]) => [key, cloneValue(item)]),
    )
  }

  return value
}

const toPlainHeaders = (headers?: RequestHeaders): RequestHeaders => {
  if (!headers) return {}

  const headerSource = typeof (headers as { toJSON?: unknown }).toJSON === 'function'
    ? (headers as { toJSON: () => RequestHeaders }).toJSON()
    : headers

  return { ...headerSource }
}

const withoutSigningHeaders = (headers?: RequestHeaders): RequestHeaders => {
  return Object.fromEntries(
    Object.entries(toPlainHeaders(headers))
      .filter(([key]) => !SIGN_HEADER_NAMES.has(key.toLowerCase())),
  )
}

const cloneRequestConfig = (config: SignV3RequestConfig): SignV3RequestConfig => {
  return {
    ...config,
    data: cloneValue(config.data),
    headers: withoutSigningHeaders(config.headers),
    params: cloneValue(config.params),
  }
}

export const isSignV3Params = (signParams?: readonly string[]): boolean => {
  return Boolean(
    signParams?.length
    && signParams.every((path) => /^(body|headers)\.[^.]+/.test(path)),
  )
}

export const createSignV3Runtime = (options: SignClientOptions): SignV3Runtime => {
  const signClient = createSignClient(options)

  return {
    async createHeaders({
      url,
      body,
      headers = {},
      signParams,
    }: CreateHeadersInput): Promise<SignV3Headers> {
      if (!signParams.length || signParams.some((path) => !SIGN_PATH_PATTERN.test(path))) {
        throw new Error('签名参数路径无效')
      }

      const requestContext = { body, headers: toPlainHeaders(headers) }
      const token = typeof requestContext.headers._admin_tk === 'string'
        ? requestContext.headers._admin_tk
        : ''
      const signature = await signClient.createRequestSignature({
        token,
        url,
        requestContext,
        signParams: [...signParams],
      })
      const timestamp = signature.headers._yy_ts
      const nonce = signature.headers._yy_nonce
      const signatureText = buildSignatureText({
        timestamp,
        nonce,
        token,
        urlLastSegment: extractUrlLastSegment(url),
        signParamValues: extractSignParamValues(requestContext, signParams),
        salt: options.salt,
      })

      return {
        ...signature.headers,
        _yy_sign_text: encodeURIComponent(signatureText),
      }
    },
    processResponse(data: unknown, retryCount: 0 | 1): ResponseDecision {
      return signClient.processResponse(data, retryCount)
    },
  }
}

export const snapshotSignV3Request = (
  config: SignV3RequestConfig,
): SignV3RequestConfig => {
  const snapshot = cloneRequestConfig(config)
  delete snapshot.__isSignV3Request
  delete snapshot.__sign1504RetryCount
  delete snapshot.__yyOriginalRequest
  return snapshot
}

export const rebuildSignV3Request = (
  snapshot: SignV3RequestConfig,
  retryCount: 1,
): SignV3RequestConfig => {
  return {
    ...cloneRequestConfig(snapshot),
    __isSignV3Request: true,
    __sign1504RetryCount: retryCount,
    __yyOriginalRequest: snapshot,
  }
}
